import React, { Fragment } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import Heading from '../Heading/heading';
import Icon from '../Icon/icon';
import { IconNames } from '../../util/types';
import IconButton from '../IconButton/iconButton';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  backdrop: 'static' | 'dismissible' | 'none';
  content: React.ReactNode;
  footer?: React.ReactNode;
  icon?: boolean;
  iconName?: IconNames;
  iconComponent?: React.ReactNode;
  dismissible?: boolean;
  horizontalRule?: boolean;
}

const Modal = ({
  open = false,
  onClose,
  title = '',
  backdrop = 'static',
  content,
  footer,
  icon = false,
  iconComponent,
  iconName = undefined,
  dismissible = true,
  horizontalRule = true
}: ModalProps) => {
  const getIconComponent = (): React.ReactNode | null => {
    if (iconComponent) {
      return <>{iconComponent}</>;
    } else if (icon && iconName) {
      return <Icon name={iconName} size="medium" />;
    } else {
      return null;
    }
  };

  const getOnClose = () => {
    return backdrop === 'dismissible' ? onClose() : true;
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        onClose={getOnClose}
        static={true}
        className="preflight ds-relative ds-z-20 focus:ds-outline-none"
        aria-labelledby="modal-title"
      >
        {backdrop !== 'none' && (
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="ds-fixed ds-inset-0 ds-bg-neutrals-transparent-medium ds-transition-opacity"
              aria-hidden="true"
            />
          </TransitionChild>
        )}
        <div className="ds-fixed ds-inset-0 ds-z-20 ds-w-screen ds-overflow-y-auto">
          <div className="ds-flex ds-min-h-full ds-items-center ds-justify-center ds-p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="ds-flex ds-max-h-[90vh] ds-min-w-full ds-max-w-full ds-transform ds-flex-col ds-overflow-y-scroll ds-rounded-lg ds-bg-neutrals-white ds-text-left ds-shadow-modal sm:ds-min-w-[15rem] sm:ds-max-w-[55%]">
                {/* Modal Heading */}

                <div
                  className={`ds-flex ds-items-center ds-py-4 ${dismissible ? 'ds-pl-5 ds-pr-3' : 'ds-px-5'}`}
                >
                  {icon && <div className="ds-mr-3">{getIconComponent()}</div>}
                  <div className="ds-flex-grow">
                    <Heading
                      text={title}
                      headingTag="h2"
                      size="medium"
                      hasMarginBottom={false}
                      id="modal-title"
                    />
                  </div>
                  {dismissible && (
                    <IconButton
                      variant="default"
                      iconSize="small"
                      iconName="close"
                      hasTooltip={false}
                      label="Close"
                      disabled={false}
                      onClick={() => onClose()}
                    />
                  )}
                </div>
                {horizontalRule && <hr className="ds-border-neutrals-light" />}

                {/* Modal Content */}
                {content && (
                  <div
                    className={`ds-h-auto ds-max-h-[48.6rem] ds-grow ds-overflow-y-scroll ds-px-5 ${footer ? 'ds-py-4' : 'ds-pb-5 ds-pt-4'}`}
                  >
                    {content}
                  </div>
                )}

                {/* Modal Footer */}

                {footer && (
                  <div className="ds-flex ds-justify-end ds-gap-x-4 ds-px-5 ds-py-4">
                    {footer}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
